import type { Struct } from 'superstruct';
import { dynamic, never } from 'superstruct';

import { withMessage } from './withMessage';

const ACCEPTED_RATIOS = [1, 1.91, 0.8];
const RATIO_TOLERANCE = 0.02;

function getAspectRatio(img: { width: number; height: number }) {
	if (!img.width || !img.height) return null;
	return +(img.width / img.height).toFixed(2);
}

export function validateCarouselCardImages<T, V>(imageStruct: Struct<T, V>) {
	return dynamic((value) => {
		if (!Array.isArray(value)) return imageStruct;
		const foundRatios: number[] = [];
		let hasValid = false;
		for (const img of value) {
			const ratio = getAspectRatio(img);
			if (ratio === null) continue;
			for (const allowed of ACCEPTED_RATIOS) {
				if (Math.abs(ratio - allowed) < RATIO_TOLERANCE) {
					if (
						foundRatios.some((r) => Math.abs(r - allowed) < RATIO_TOLERANCE)
					) {
						return withMessage(
							never(),
							'validations:Carousel Card Unique Aspect Ratios'
						);
					}
					foundRatios.push(allowed);
					hasValid = true;
				}
			}
		}
		if (!hasValid) {
			return withMessage(
				never(),
				'validations:Carousel Card Valid Aspect Ratio'
			);
		}
		return imageStruct;
	});
}

export function validateCarouselCardsAspectRatios<T, V>(
	cardStruct: Struct<T, V>
) {
	return dynamic((cards) => {
		if (!Array.isArray(cards)) return cardStruct;
		let baseSet: number[] | null = null;
		for (const card of cards) {
			if (!Array.isArray(card.images)) return cardStruct;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const ratios = (card.images as any[])
				.map(getAspectRatio)
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				.filter((r: any): r is number => r !== null)
				.map((r: number) =>
					ACCEPTED_RATIOS.find(
						(allowed) => Math.abs(r - allowed) < RATIO_TOLERANCE
					)
				)
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				.filter((r: any): r is number => r !== null);

			const unique: number[] = [...new Set(ratios)].sort((a, b) => a - b);
			if (!baseSet) {
				baseSet = unique;
			} else if (
				unique.length !== baseSet.length ||
				unique.some(
					(r: number, i: number) =>
						Math.abs(r - (baseSet![i] ?? 0)) > RATIO_TOLERANCE
				)
			) {
				return withMessage(never(), 'validations:Carousel Card Aspect Ratios');
			}
		}
		return cardStruct;
	});
}
